import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "../api-client";
import type {
  MargauxPromptVersion,
  PreviewInput,
  PreviewResult,
} from "./prompt-types";

const ACTIVE_KEY = ["staff", "margaux-prompt", "active"] as const;
const HISTORY_KEY = ["staff", "margaux-prompt", "history"] as const;

export function useActivePromptQuery() {
  return useQuery<MargauxPromptVersion>({
    queryKey: ACTIVE_KEY,
    queryFn: () =>
      getApiClient().get<MargauxPromptVersion>("/staff/margaux/prompt/active"),
    staleTime: 30 * 1000,
  });
}

export function usePromptHistoryQuery() {
  return useQuery<MargauxPromptVersion[]>({
    queryKey: HISTORY_KEY,
    queryFn: () =>
      getApiClient().get<MargauxPromptVersion[]>(
        "/staff/margaux/prompt/history",
      ),
    staleTime: 30 * 1000,
  });
}

export function useCreateDraftMutation() {
  const qc = useQueryClient();
  return useMutation<MargauxPromptVersion, Error, { body: string }>({
    mutationFn: (input) =>
      getApiClient().post<MargauxPromptVersion>(
        "/staff/margaux/prompt/draft",
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: HISTORY_KEY });
    },
  });
}

export function usePublishVersionMutation() {
  const qc = useQueryClient();
  return useMutation<MargauxPromptVersion, Error, { id: string }>({
    mutationFn: ({ id }) =>
      getApiClient().post<MargauxPromptVersion>(
        `/staff/margaux/prompt/${id}/publish`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ACTIVE_KEY });
      qc.invalidateQueries({ queryKey: HISTORY_KEY });
    },
  });
}

export function useRunPreviewMutation() {
  return useMutation<PreviewResult, Error, PreviewInput>({
    mutationFn: (input) =>
      getApiClient().post<PreviewResult>(
        "/staff/margaux/prompt/preview",
        input,
      ),
  });
}
